<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useNIRApplicationsStore } from "@/domains/home/services/nir/registration/stores";
import BulkDataView from "@/domains/home/services/nir/registration/views/BulkDataView.vue";
import Pagination from "@/commons/components/Pagination.vue";
import { dateTimeFormat } from "@/commons/composables/transformations.ts";
import {useFiles} from "@/commons/composables/files.ts";

const route = useRoute();
const router = useRouter();
const store = useNIRApplicationsStore();

const bulkId = String(route.params.id);
const searchFilter = ref("");
const statusFilter = ref("");
const page = ref(1);
const limit = ref(16);
const loading = ref(false);
const fileStore = useFiles()

const selectedBulk = computed(() =>
    store.bulkApplications?.find(b => b.id === bulkId)
);

onMounted(() => {
  if (!selectedBulk.value) {
    store.fetchBulkApplications(1, 100);
    store.fetchBulkApplicationStatistics(bulkId);
  }
  fetchRows();
});

function fetchRows() {
  loading.value = true;
  store.fetchBulkApplicationRows(
      bulkId,
      searchFilter.value,
      statusFilter.value,
      page.value,
      limit.value
  ).finally(() => {
    loading.value = false;
  });
}

function exportFailedJobs(){
  fileStore.download('/v1/applications/bulk/export-failed-rows', bulkId, "export_failed_entities.xls", 'excel')
}

function onPageUpdate(newPage: number) {
  page.value = newPage;
  fetchRows();
}

function goBack() {
  router.push({ name: "nir-bulk-applications" });
}

function rowField(rowData: Record<string, string> | undefined, candidates: string[]): string {
  if (!rowData) return "";
  for (const candidate of candidates) {
    for (const key of Object.keys(rowData)) {
      if (key.toLowerCase() === candidate.toLowerCase()) {
        const value = rowData[key];
        if (value) return String(value);
      }
    }
  }
  return "";
}
</script>

<template>
  <div class="flex-1 flex flex-col p-2 gap-2">
    <div class="flex items-center">
      <button class="btn-sm-primary" @click="goBack">
        <i class="fa fa-arrow-left"></i> Back to Bulk Applications
      </button>
    </div>

    <BulkDataView :data="selectedBulk" />

    <div class="grid grid-cols-4 gap-2 py-2" v-if="store.bulkApplicationStats">
      <div class="flex flex-col text-gray-500 bg-white rounded-lg shadow p-3 text-center justify-center items-center">
        <label class="text-2xl font-bold">{{store.bulkApplicationStats.processing}}</label>
        <label class="text-sm">Processing</label>
      </div>
      <div class="flex flex-col text-success-600 bg-white rounded-lg shadow p-3 text-center justify-center items-center">
        <label class="text-2xl font-bold">{{store.bulkApplicationStats.processed}}</label>
        <label class="text-sm">Processed</label>
      </div>
      <div class="flex flex-col text-danger-600 bg-white rounded-lg shadow p-3 text-center justify-center items-center">
        <label class="text-2xl font-bold">{{store.bulkApplicationStats.failed}}</label>
        <label class="text-sm">Failed</label>
      </div>
      <div class="flex flex-col bg-white rounded-lg shadow p-3 text-center justify-center items-center">
        <label class="text-2xl font-bold">{{store.bulkApplicationStats.total}}</label>
        <label class="text-sm">Total</label>
      </div>
    </div>

    <div class="grid grid-cols-4 gap-2 py-2">
      <input
          v-model="searchFilter"
          @change="fetchRows"
          type="text"
          placeholder="Search tracking number..."
          class="col-span-2 w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"/>
      <select
          v-model="statusFilter"
          @change="fetchRows"
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary">
        <option value="">All Statuses</option>
        <option value="PENDING">Pending</option>
        <option value="PROCESSING">Processing</option>
        <option value="PROCESSED">Processed</option>
        <option value="FAILED">Failed</option>
      </select>

      <button class="btn-primary" @click="exportFailedJobs">
        <i class="fas fa-file-excel"></i> Export Failed Rows

      </button>
    </div>

    <div class="bg-white rounded shadow flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
          <tr>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Row #
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Entity Name
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Registration No.
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Type
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Status
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Error
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Date
            </th>
          </tr>
          </thead>
          <tbody>
          <tr
              v-for="(row, idx) in store.bulkApplicationRows"
              :key="row.id || idx"
              class="hover:bg-gray-50 transition">
            <td class="py-3 px-4 border-b border-gray-200 font-bold">
              <span>{{ row.row }}</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200 font-semibold">
              <template v-if="rowField(row.rowData, ['Entity Name', 'Name', 'name'])">
                {{ rowField(row.rowData, ['Entity Name', 'Name', 'name']) }}
              </template>
              <span v-else class="text-gray-400">--</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200">
              <template v-if="rowField(row.rowData, ['Current Registration Number', 'Registration Number', 'BRN', 'ERN'])">
                {{ rowField(row.rowData, ['Current Registration Number', 'Registration Number', 'BRN', 'ERN']) }}
              </template>
              <span v-else class="text-gray-400">--</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200">
              <template v-if="rowField(row.rowData, ['Entity Type', 'Type'])">
                {{ rowField(row.rowData, ['Entity Type', 'Type']) }}
                <span v-if="rowField(row.rowData, ['Entity Sub Type', 'Sub Type'])" class="text-gray-400">
                  / {{ rowField(row.rowData, ['Entity Sub Type', 'Sub Type']) }}
                </span>
              </template>
              <span v-else class="text-gray-400">--</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200">
                <span class="text-xs font-medium px-3 py-1 rounded-full inline-block"
                      :class="{
                        'bg-gray-100 text-gray-600': row.status === 'PENDING',
                        'bg-primary-100 text-primary-800': row.status === 'PROCESSING',
                        'bg-success-100 text-success-800': row.status === 'PROCESSED',
                        'bg-danger-100 text-danger-800': row.status === 'FAILED'
                      }">
                  {{ row.status }}
                </span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200 text-danger-600 max-w-xs">
              <span v-if="row.errorMessage" class="block truncate" :title="row.errorMessage">{{ row.errorMessage }}</span>
              <span v-else class="text-gray-400">--</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200 text-gray-400">
              <span v-if="row.createdAt">{{ dateTimeFormat(row.createdAt) }}</span>
            </td>
          </tr>
          <tr v-if="loading">
            <td colspan="7" class="py-8 text-center text-gray-500">
              <i class="fa fa-spinner fa-spin mr-2"></i>Loading...
            </td>
          </tr>
          <tr v-else-if="store.bulkApplicationRows && store.bulkApplicationRows.length === 0">
            <td colspan="7" class="py-8 text-center text-gray-500">
              No applications in this bulk match the filters
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <Pagination
          :pagination="store.pagination"
          :page="page"
          :limit="limit"
          label="applications"
          @update="onPageUpdate"/>
    </div>
  </div>
</template>

<style scoped>

</style>
